import Modal from "react-modal";
import Swal from "sweetalert2";
import {
  createOrUpdateCompanyApi,
  getCompanyApi,
  removeCompanyImagedata,
} from "../../../Services/Superadmin/Superadmin";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { ThreeDots } from "react-loader-spinner";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

Modal.setAppElement("#root");

const System = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [companiesData, setCompaniesData] = useState({});

  const validationSchema = Yup.object().shape({
    panelName: Yup.string()
      .required("Panel Name is required")
      .min(3, "Panel Name must be at least 3 characters"),
    loginUrl: Yup.string()
      .required("Login URL is required")
      .url("Enter a valid URL"),
    logo: Yup.string().required("Logo is required"),
    favicon: Yup.string().required("Favicon is required"),
    loginImage: Yup.string().required("Login Image is required"),
  });

  const changeFavicon = (iconPath) => {
    const link =
      document.querySelector('link[rel*="icon"]') ||
      document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "icon";
    link.href = iconPath;
    document.getElementsByTagName("head")[0].appendChild(link);
  };

  const fetchCompaniesData = async () => {
    try {
      const res = await getCompanyApi({});
      if (res.data) {
        setCompaniesData(res.data);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load company data.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompaniesData();
  }, []);

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleSubmit = async (values) => {
    try {
      const res = await createOrUpdateCompanyApi(values);
      if (res.status) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.message || "Company settings updated successfully!",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: res.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while submitting the form. Please try again.",
      });
    }
  };

  const RemoveImage = async (type) => {
    try {
      const data = { type };
      const res = await removeCompanyImagedata(data);
      if (res.status) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.message || "Image removed!",
        });
        // Optionally refresh data so previews update:
        fetchCompaniesData();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: res.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while submitting the form. Please try again.",
      });
    }
  };

  return (
    <div
      className="container-fluid "
    
    >
      <div className="card transaction-table custom-bg">
     
      <div className="row justify-content-center">
        <div className="col-xl-12 col-lg-12">
          <div className="card shadow-lg border-0 rounded-4">
            {/* HEADER */}
            <div className="card-header  rounded-top-4 py-3 px-4">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between">
                <div>
                  <h4 className="mb-1">System Settings</h4>
                  <p className="mb-0 small opacity-75">
                    Manage your panel name, login URL, and branding assets used
                    across the application.
                  </p>
                </div>
              </div>
            </div>

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center bg-white"
                style={{ height: "320px" }}
              >
                <div className="text-center">
                  <ThreeDots
                    visible={true}
                    height="80"
                    width="80"
                    color="#0d6efd"
                    radius="9"
                    ariaLabel="three-dots-loading"
                  />
                  <div className="mt-3 text-muted small">
                    Loading company settings...
                  </div>
                </div>
              </div>
            ) : (
              <div className="card-body px-4 pt-4 pb-4 bg-white">
                <Formik
                  enableReinitialize={true}
                  initialValues={{
                    panelName: companiesData?.panelName || "",
                    loginUrl: companiesData?.loginUrl || "",
                    logo: companiesData?.logo || "",
                    favicon: companiesData?.favicon || "",
                    loginImage: companiesData?.loginImage || "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, errors, touched, values }) => (
                    <Form>
                      {/* GENERAL INFO SECTION */}
                      <div className="mb-4 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">General Information</h5>
                          <span className="text-muted small">
                            Basic identity of your panel.
                          </span>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label
                              htmlFor="panelName"
                              className="form-label fw-semibold"
                            >
                              Panel Name
                            </label>
                            <Field
                              type="text"
                              name="panelName"
                              id="panelName"
                              className={`form-control ${
                                errors.panelName && touched.panelName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="Enter Panel Name"
                            />
                            {errors.panelName && touched.panelName && (
                              <div className="invalid-feedback">
                                {errors.panelName}
                              </div>
                            )}
                            <small className="text-muted">
                              This name may appear on the login page and in the
                              browser tab.
                            </small>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label
                              htmlFor="loginUrl"
                              className="form-label fw-semibold"
                            >
                              Login URL
                            </label>
                            <Field
                              type="text"
                              name="loginUrl"
                              id="loginUrl"
                              className={`form-control ${
                                errors.loginUrl && touched.loginUrl
                                  ? "is-invalid"
                                  : ""
                              }`}
                              placeholder="https://yourdomain.com/login"
                            />
                            {errors.loginUrl && touched.loginUrl && (
                              <div className="invalid-feedback">
                                {errors.loginUrl}
                              </div>
                            )}
                            <small className="text-muted">
                              This is the URL where users go to log into the
                              panel.
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* BRANDING ASSETS SECTION */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">Branding Assets</h5>
                          <span className="text-muted small">
                            Upload logo, favicon and login page image.
                          </span>
                        </div>

                        <div className="row">
                          {/* LOGO */}
                          <div className="col-md-4 mb-3">
                            <div className="border rounded-3 p-3 h-100">
                              <label
                                htmlFor="logo"
                                className="form-label fw-semibold"
                              >
                                Logo
                              </label>
                              <input
                                type="file"
                                id="logo"
                                name="logo"
                                className={`form-control ${
                                  errors.logo && touched.logo
                                    ? "is-invalid"
                                    : ""
                                }`}
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const base64 = await convertToBase64(file);
                                  setFieldValue("logo", base64);
                                }}
                              />
                              {errors.logo && touched.logo && (
                                <div className="invalid-feedback">
                                  {errors.logo}
                                </div>
                              )}
                              <small className="text-muted d-block mt-1">
                                Recommended: transparent PNG for best results.
                              </small>

                              {values.logo && (
                                <div className="mt-3 position-relative d-inline-block">
                                  <img
                                    src={values.logo}
                                    alt="Current Logo"
                                    width="120"
                                    className="rounded border bg-white"
                                  />
                                  <button
                                    type="button"
                                    className="position-absolute btn btn-danger rounded-circle p-1"
                                    style={{
                                      top: "-8px",
                                      right: "-8px",
                                      width: "24px",
                                      height: "24px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: "2px solid white",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => RemoveImage(1)}
                                    title="Remove Logo"
                                  >
                                    <X size={12} color="white" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* FAVICON */}
                          <div className="col-md-4 mb-3">
                            <div className="border rounded-3 p-3 h-100">
                              <label
                                htmlFor="favicon"
                                className="form-label fw-semibold"
                              >
                                Favicon
                              </label>
                              <input
                                type="file"
                                id="favicon"
                                name="favicon"
                                className={`form-control ${
                                  errors.favicon && touched.favicon
                                    ? "is-invalid"
                                    : ""
                                }`}
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const base64 = await convertToBase64(file);
                                  setFieldValue("favicon", base64);
                                  changeFavicon(base64);
                                }}
                              />
                              {errors.favicon && touched.favicon && (
                                <div className="invalid-feedback">
                                  {errors.favicon}
                                </div>
                              )}
                              <small className="text-muted d-block mt-1">
                                Small icon shown in browser tab (e.g. 32×32px).
                              </small>

                              {values.favicon && (
                                <div className="mt-3 position-relative d-inline-block">
                                  <img
                                    src={values.favicon}
                                    alt="Current Favicon"
                                    width="64"
                                    className="rounded border bg-white"
                                  />
                                  <button
                                    type="button"
                                    className="position-absolute btn btn-danger rounded-circle p-1"
                                    style={{
                                      top: "-8px",
                                      right: "-8px",
                                      width: "24px",
                                      height: "24px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: "2px solid white",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => RemoveImage(2)}
                                    title="Remove Favicon"
                                  >
                                    <X size={12} color="white" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* LOGIN IMAGE */}
                          <div className="col-md-4 mb-3">
                            <div className="border rounded-3 p-3 h-100">
                              <label
                                htmlFor="loginImage"
                                className="form-label fw-semibold"
                              >
                                Login Image
                              </label>
                              <input
                                type="file"
                                id="loginImage"
                                name="loginImage"
                                className={`form-control ${
                                  errors.loginImage && touched.loginImage
                                    ? "is-invalid"
                                    : ""
                                }`}
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const base64 = await convertToBase64(file);
                                  setFieldValue("loginImage", base64);
                                }}
                              />
                              {errors.loginImage && touched.loginImage && (
                                <div className="invalid-feedback">
                                  {errors.loginImage}
                                </div>
                              )}
                              <small className="text-muted d-block mt-1">
                                Background/side image displayed on the login
                                screen.
                              </small>

                              {values.loginImage && (
                                <div className="mt-3 position-relative d-inline-block">
                                  <img
                                    src={values.loginImage}
                                    alt="Current Login"
                                    width="140"
                                    className="rounded border bg-white"
                                  />
                                  <button
                                    type="button"
                                    className="position-absolute btn btn-danger rounded-circle p-1"
                                    style={{
                                      top: "-8px",
                                      right: "-8px",
                                      width: "24px",
                                      height: "24px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: "2px solid white",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => RemoveImage(3)}
                                    title="Remove Login Image"
                                  >
                                    <X size={12} color="white" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>



                      <div className="row">
                        <div className="col-12 text-center">
                          <button
                            type="submit"
                            className="btn btn-primary w-100">
                            Save
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
      </div>

  );
};

export default System;
